import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { toast } from 'react-toastify';
import './TemplateGenerator.css';

/**
 * TemplateGenerator - AI-driven mallgenerering
 *
 * Wizard-flöde:
 * 1. Ladda upp CSV med rubriker
 * 2. Ange metadata (namn, klinik, prompt)
 * 3. Generera prompter med GPT
 * 4. Preview och redigera
 * 5. Spara mall
 */
function TemplateGenerator({ isOpen, onClose, clinics, onTemplateCreated }) {
  // Wizard state
  const [step, setStep] = useState(1);

  // Data state
  const [headers, setHeaders] = useState([]);
  const [metadata, setMetadata] = useState({
    templateName: '',
    clinicId: '',
    templatePrompt: ''
  });
  const [generatedSections, setGeneratedSections] = useState([]);
  const [inspirationTemplates, setInspirationTemplates] = useState([]);

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [regeneratingIndex, setRegeneratingIndex] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen]);

  const resetState = () => {
    setStep(1);
    setHeaders([]);
    setMetadata({ templateName: '', clinicId: '', templatePrompt: '' });
    setGeneratedSections([]);
    setInspirationTemplates([]);
    setIsGenerating(false);
    setIsSaving(false);
    setError(null);
    setEditingIndex(null);
    setRegeneratingIndex(null);
  };

  // ========================================================================
  // CSV PARSING
  // ========================================================================
  const parseCsvHeaders = (csvText) => {
    // Remove BOM if present
    let cleanText = csvText.replace(/^\uFEFF/, '');
    cleanText = cleanText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    const lines = cleanText.split('\n');
    const headers = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed) {
        // Ta första kolumnen (före eventuellt ; eller ,)
        const parts = trimmed.split(/[;,]/);
        const header = parts[0].replace(/^["']|["']$/g, '').trim();
        if (header) {
          headers.push(header);
        }
      }
    }

    return headers;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Vänligen välj en CSV-fil');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target.result;
      const parsedHeaders = parseCsvHeaders(csvText);

      if (parsedHeaders.length === 0) {
        toast.error('Inga rubriker hittades i CSV-filen');
        return;
      }

      if (parsedHeaders.length > 30) {
        toast.error('Max 30 rubriker tillåtet');
        return;
      }

      setHeaders(parsedHeaders);
      toast.success(`${parsedHeaders.length} rubriker laddade`);
    };

    reader.readAsText(file, 'UTF-8');
  };

  const removeHeader = (index) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const addHeader = () => {
    const newHeader = prompt('Ange rubriknamn:');
    if (newHeader && newHeader.trim()) {
      setHeaders([...headers, newHeader.trim()]);
    }
  };

  // ========================================================================
  // GENERATION
  // ========================================================================
  const handleGenerate = async () => {
    if (!metadata.templateName.trim()) {
      toast.error('Ange ett mallnamn');
      return;
    }

    if (!metadata.clinicId) {
      toast.error('Välj en klinik');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setStep(3); // Gå till "generating" steg

    try {
      const response = await apiClient.post('/api/template-generator/generate-prompts', {
        headers: headers,
        template_name: metadata.templateName,
        template_prompt: metadata.templatePrompt,
        clinic_id: parseInt(metadata.clinicId)
      });

      setGeneratedSections(response.data.sections || []);
      setInspirationTemplates(response.data.inspiration_templates || []);
      setStep(4); // Gå till preview
      toast.success('Prompter genererade!');

    } catch (err) {
      console.error('Error generating prompts:', err);
      setError(err.response?.data?.msg || 'Fel vid generering');
      toast.error('Generering misslyckades');
      setStep(2); // Gå tillbaka till metadata-steg
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateSection = async (index) => {
    const section = generatedSections[index];
    setRegeneratingIndex(index);

    try {
      const feedback = prompt('Eventuell feedback för regenerering (lämna tomt för standard):');

      const response = await apiClient.post('/api/template-generator/regenerate-section', {
        header: section.title,
        template_name: metadata.templateName,
        template_prompt: metadata.templatePrompt,
        other_sections: generatedSections.filter((_, i) => i !== index),
        feedback: feedback || ''
      });

      // Uppdatera sektionen
      const updated = [...generatedSections];
      updated[index] = { ...section, prompt: response.data.prompt };
      setGeneratedSections(updated);
      toast.success('Sektion regenererad');

    } catch (err) {
      console.error('Error regenerating section:', err);
      toast.error('Regenerering misslyckades');
    } finally {
      setRegeneratingIndex(null);
    }
  };

  const handleEditSection = (index, newPrompt) => {
    const updated = [...generatedSections];
    updated[index] = { ...updated[index], prompt: newPrompt };
    setGeneratedSections(updated);
    setEditingIndex(null);
  };

  // ========================================================================
  // SAVE
  // ========================================================================
  const handleSave = async () => {
    setIsSaving(true);

    try {
      const payload = {
        template_name: metadata.templateName,
        template_prompt: metadata.templatePrompt,
        clinic_id: parseInt(metadata.clinicId),
        is_pmo_template: false,
        sections: generatedSections.map((s, idx) => ({
          section_title: s.title,
          content: s.prompt,
          order: idx,
          tabs_needed: 0
        }))
      };

      await apiClient.post('/api/journalmallar', payload);
      toast.success('Mall skapad!');

      if (onTemplateCreated) {
        onTemplateCreated();
      }

      onClose();

    } catch (err) {
      console.error('Error saving template:', err);
      toast.error(err.response?.data?.msg || 'Sparning misslyckades');
    } finally {
      setIsSaving(false);
    }
  };

  // ========================================================================
  // RENDER
  // ========================================================================
  if (!isOpen) return null;

  return (
    <div className="tg-overlay" onClick={onClose}>
      <div className="tg-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tg-header">
          <h2>Generera Journalmall med AI</h2>
          <button className="tg-close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* Progress indicator */}
        <div className="tg-progress">
          <div className={`tg-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <span className="tg-step-number">1</span>
            <span className="tg-step-label">Rubriker</span>
          </div>
          <div className={`tg-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <span className="tg-step-number">2</span>
            <span className="tg-step-label">Metadata</span>
          </div>
          <div className={`tg-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
            <span className="tg-step-number">3</span>
            <span className="tg-step-label">Generera</span>
          </div>
          <div className={`tg-step ${step >= 4 ? 'active' : ''}`}>
            <span className="tg-step-number">4</span>
            <span className="tg-step-label">Granska</span>
          </div>
        </div>

        {/* Content */}
        <div className="tg-content">
          {/* Step 1: Upload CSV */}
          {step === 1 && (
            <div className="tg-step-content">
              <h3>Steg 1: Ladda upp rubriker</h3>
              <p className="tg-description">
                Ladda upp en CSV-fil med rubriknamn (en rubrik per rad).
                Du kan också lägga till rubriker manuellt.
              </p>

              <div className="tg-upload-area">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  id="tg-csv-input"
                  style={{ display: 'none' }}
                />
                <label htmlFor="tg-csv-input" className="tg-upload-btn">
                  Välj CSV-fil
                </label>
                <span className="tg-upload-hint">eller dra och släpp här</span>
              </div>

              {headers.length > 0 && (
                <div className="tg-headers-list">
                  <h4>Laddade rubriker ({headers.length}):</h4>
                  <div className="tg-headers-grid">
                    {headers.map((header, index) => (
                      <div key={index} className="tg-header-item">
                        <span>{header}</span>
                        <button
                          className="tg-remove-btn"
                          onClick={() => removeHeader(index)}
                          title="Ta bort"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="tg-add-header-btn" onClick={addHeader}>
                    + Lägg till rubrik
                  </button>
                </div>
              )}

              <div className="tg-actions">
                <button className="tg-btn secondary" onClick={onClose}>
                  Avbryt
                </button>
                <button
                  className="tg-btn primary"
                  onClick={() => setStep(2)}
                  disabled={headers.length === 0}
                >
                  Nästa
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Metadata */}
          {step === 2 && (
            <div className="tg-step-content">
              <h3>Steg 2: Mallinfo</h3>
              <p className="tg-description">
                Ange namn och inställningar för mallen.
              </p>

              <div className="tg-form">
                <div className="tg-form-group">
                  <label>Mallnamn *</label>
                  <input
                    type="text"
                    value={metadata.templateName}
                    onChange={(e) => setMetadata({ ...metadata, templateName: e.target.value })}
                    placeholder="T.ex. Primärvård Standard"
                  />
                </div>

                <div className="tg-form-group">
                  <label>Klinik *</label>
                  <select
                    value={metadata.clinicId}
                    onChange={(e) => setMetadata({ ...metadata, clinicId: e.target.value })}
                  >
                    <option value="">-- Välj klinik --</option>
                    {clinics.map(c => (
                      <option key={c.clinic_id} value={c.clinic_id}>
                        {c.clinic_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="tg-form-group">
                  <label>Mallspecifik prompt (valfritt)</label>
                  <textarea
                    value={metadata.templatePrompt}
                    onChange={(e) => setMetadata({ ...metadata, templatePrompt: e.target.value })}
                    placeholder="Beskriv eventuella speciella instruktioner för denna mall, t.ex. 'Anpassad för psykologmottagning' eller 'Fokus på somatiska besvär'"
                    rows={4}
                  />
                  <span className="tg-hint">
                    Denna prompt kommer att inkluderas i GPT-instruktionerna för mallens sektioner.
                  </span>
                </div>
              </div>

              <div className="tg-summary">
                <h4>Sammanfattning:</h4>
                <p><strong>Antal rubriker:</strong> {headers.length}</p>
                <p><strong>Rubriker:</strong> {headers.join(', ')}</p>
              </div>

              {error && <div className="tg-error">{error}</div>}

              <div className="tg-actions">
                <button className="tg-btn secondary" onClick={() => setStep(1)}>
                  Tillbaka
                </button>
                <button
                  className="tg-btn primary"
                  onClick={handleGenerate}
                  disabled={!metadata.templateName || !metadata.clinicId}
                >
                  Generera prompter
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Generating */}
          {step === 3 && (
            <div className="tg-step-content tg-generating">
              <div className="tg-spinner"></div>
              <h3>Genererar prompter...</h3>
              <p>GPT analyserar dina rubriker och skapar optimala instruktioner.</p>
              <p className="tg-hint">Detta kan ta upp till 30 sekunder.</p>
            </div>
          )}

          {/* Step 4: Preview */}
          {step === 4 && (
            <div className="tg-step-content">
              <h3>Steg 4: Granska och redigera</h3>

              {inspirationTemplates.length > 0 && (
                <p className="tg-inspiration-info">
                  Inspirerades av: {inspirationTemplates.join(', ')}
                </p>
              )}

              <div className="tg-sections-list">
                {generatedSections.map((section, index) => (
                  <div key={index} className="tg-section-item">
                    <div className="tg-section-header">
                      <h4>{section.title}</h4>
                      <div className="tg-section-actions">
                        <button
                          className="tg-edit-btn"
                          onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                          title="Redigera"
                        >
                          {editingIndex === index ? 'Stäng' : 'Redigera'}
                        </button>
                        <button
                          className="tg-regenerate-btn"
                          onClick={() => handleRegenerateSection(index)}
                          disabled={regeneratingIndex === index}
                          title="Regenerera med GPT"
                        >
                          {regeneratingIndex === index ? 'Genererar...' : 'Regenerera'}
                        </button>
                      </div>
                    </div>

                    {editingIndex === index ? (
                      <textarea
                        className="tg-section-edit"
                        value={section.prompt}
                        onChange={(e) => handleEditSection(index, e.target.value)}
                        rows={4}
                      />
                    ) : (
                      <p className="tg-section-prompt">{section.prompt}</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="tg-actions">
                <button className="tg-btn secondary" onClick={() => setStep(2)}>
                  Tillbaka
                </button>
                <button
                  className="tg-btn primary"
                  onClick={handleSave}
                  disabled={isSaving || generatedSections.length === 0}
                >
                  {isSaving ? 'Sparar...' : 'Spara mall'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TemplateGenerator;
